import { gql } from '@reverbdotcom/commons/src/gql';
import { FetchResult } from '@apollo/client';
import { ChildProps } from '@apollo/client/react/hoc';
import { omit } from 'lodash';
import React from 'react';
import { WithRouterProps, withRouter } from 'react-router';
import { ExecutionResult } from 'graphql';

import { IUserContext, withUserContext } from '@reverbdotcom/commons';
import RouterHistory from '@reverbdotcom/commons/src/cms/lib/router_history';
import { connect } from '@reverbdotcom/commons/src/connect';
import { IUrl, useURL, withUrlContext, WithUrlProps } from '@reverbdotcom/commons/src/url_context';
import { isCSP, isShopPage } from '@reverbdotcom/commons/src/url_helpers';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';

import {
  core_apimessages_Favorite,
  Core_FindFavoriteQuery,
  Core_FindFavoriteQueryVariables,
  core_apimessages_FavoriteType,
  reverb_feed_SearchableType,
  UndoDeleteMyFavoriteMutation,
  UpsertMyFavoriteMutation,
  core_apimessages_Error,
} from '@reverbdotcom/commons/src/gql/graphql';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { mapVarsForMarketplaceQuery } from '../marketplace/marketplace_page';
import { FOLLOW_PROMPT_PARAM } from '../shared/constants';
import { useAutoSaveFavorite } from '../shared/auto_save_favorite';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { buildClickedAddFavoriteSearch, buildClickedToastMessage } from '@reverbdotcom/commons/src/event_tracking/favorites';
import { redirectToSignin } from '@reverbdotcom/commons/src/signup_signin_helper';
import LocationUtil from '../shared/location_util';
import FavoriteToast from '@reverbdotcom/commons/src/components/favorite_toast';
import { ToastCloseType } from '@reverbdotcom/cadence/components/types';
import { RCFavoriteButtonProps } from '@reverbdotcom/cadence/components';

type Core_FindFavoriteQuery_Favorite = Core_FindFavoriteQuery['findFavorite']['favorite'];
type Core_FindFavoriteQuery_FindFavorite = Core_FindFavoriteQuery['findFavorite'];

/**
 * Represents the state of components needed to find a favorite entity and save it.
 */
export interface FindFavoriteViewModel {
  /**
   * A toast message shown after saving a favorite
   */
  isToastOpen: boolean;
  setIsToastOpen: (val: boolean) => void;

  /**
   * The potentially saveable favorite entity (i.e. a marketplace search, a CSP, etc)
   * and the current user's notification preferences for it: feed enabled, emails enabled
   */
  favorite: Core_FindFavoriteQuery_Favorite;
  setFavorite: (val: Core_FindFavoriteQuery_Favorite) => void;

  /**
   * Has the current user already saved this favorite?
   */
  isFavorite: boolean;

  /**
   * Can this favorite entity be saved? (It may not be saveable if it has unsupported query params, too many results, etc)
   */
  canFavorite: boolean;

  /**
   * Function to auto-save the current search with feedEnabled and emailEnabled set to true
   */
  autoSaveFavorite: (trackingName: string) => Promise<FetchResult<UpsertMyFavoriteMutation>>;

  /**
   * Whether or not the toast is in an error state
   */
  hasError: boolean;
  setHasError: (val: boolean) => void;

  /**
   * Error message for toast to display
   */
  errorMessage: string;
  setErrorMessage: (val: string) => void;
}

export const FindFavoriteContext = React.createContext<FindFavoriteViewModel>({
  isToastOpen: false,
  setIsToastOpen: (_) => { },
  isFavorite: false,
  canFavorite: false,
  favorite: {},
  setFavorite: (_) => { },
  autoSaveFavorite: (_) => Promise.resolve({}),
  hasError: false,
  setHasError: (_) => { },
  errorMessage: null,
  setErrorMessage: (_) => { },
});

/**
 * Use in any child component of a FindFavoriteContext.Provider
 * to access the current state of the find favorite result.
 */
export function useFindFavorite(): FindFavoriteViewModel {
  return React.useContext(FindFavoriteContext);
}

export const hasNoFiltersApplied = (url: IUrl) => {
  const queryParams = { ...url.query };
  delete queryParams[FOLLOW_PROMPT_PARAM];
  const utmParams = ['utm_campaign', 'utm_source', 'utm_medium', 'utm_term', 'utm_content'];
  utmParams.forEach(param => delete queryParams[param]);

  return !Object.keys(queryParams).length;
};

/**
 * @internal exported for testing only, do not use in application code outside this file.
 */
export function useFindFavoriteProvider(
  loading: boolean,
  findFavoriteData: Core_FindFavoriteQuery_FindFavorite,
  wasFollowPromptParamPresent: boolean,
): FindFavoriteViewModel {
  const user = useUser();
  const url = useURL();

  const isFavorite = findFavoriteData?.isFavorite || false;
  const canFavorite = findFavoriteData?.canFavorite || false;
  const favoriteFromQuery = findFavoriteData?.favorite || {};

  /**
   * Used for equality checks as a useEffect dependency.
   * JSON.stringify is normally not recommended for this,
   * but fits criteria described here: https://github.com/facebook/react/issues/14476#issuecomment-471199055
   */
  const serializedQueryData = JSON.stringify(findFavoriteData);

  const [favorite, setFavorite] = React.useState<Core_FindFavoriteQuery_Favorite>(favoriteFromQuery);

  /**
   * The first time useState is called with `favoriteFromQuery`, the query may still be loading, so the initial state value is empty.
   * When the query finishes loading, update the favorite state with non-empty data.
   */
  function syncFavoriteStateFromQuery() {
    if (!loading) { setFavorite(favoriteFromQuery); }
  }
  React.useEffect(syncFavoriteStateFromQuery, [loading, serializedQueryData]);

  const [isToastOpen, setIsToastOpen] = React.useState<boolean>(false);
  const [hasError, setHasError] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const [followPrompt, setFollowPrompt] = React.useState<boolean>(wasFollowPromptParamPresent);

  const { autoSave } = useAutoSaveFavorite({
    componentName: 'FindFavoriteProvider',
    onSuccess: (savedFavorite: core_apimessages_Favorite, trackingName: string) => {
      trackEvent(
        buildClickedAddFavoriteSearch(
          savedFavorite,
          url,
          savedFavorite.feedEnabled,
          savedFavorite.emailEnabled,
          trackingName,
        ),
      );

      setFavorite({
        ...favorite,
        ...savedFavorite,
        favorited: true,
      });

      setHasError(false);
      setIsToastOpen(true);
    },
    onError: (error: core_apimessages_Error) => {
      setHasError(true);
      setErrorMessage(error.message);
      setIsToastOpen(true);
    },
  });

  /**
   * When query data changes, ensure the toast message is hidden. This fixes a display bug:
   * - The toast message is always synced to the current `favorite` state.
   * - If a user saves a favorite, an 'Added to Favorites' toast message is shown for a few seconds.
   * - If a user changes search filters while this toast is open, this triggers a new find favorite query,
   *   which calls `setFavorite` with new data.
   * - The current `favorite` is no longer the previous `favorite` that the toast message was shown for,
   *   and the message switches from 'Added to Favorites' to 'Removed from Favorites'.
   */
  function closeToastOnFavoriteChange() {
    setIsToastOpen(false);
  }
  React.useEffect(closeToastOnFavoriteChange, [serializedQueryData]);

  // When query data has loaded, remove the follow_prompt=true param if present
  function removeFollowPromptParamIfPresent() {
    const isFollowPromptParamPresent = !!url.query[FOLLOW_PROMPT_PARAM];
    if (!loading && isFollowPromptParamPresent) {
      const queryWithoutFollowPrompt = omit(url.query, FOLLOW_PROMPT_PARAM);
      RouterHistory.replace({
        pathname: url.pathname,
        query: queryWithoutFollowPrompt,
      });
    }
  }
  React.useEffect(removeFollowPromptParamIfPresent, [loading]);

  function processAutoSaveIfEligible() {
    // exclude favorite shops, whose follow prompt is handled in the
    // shop header favorite button (see favorite_shop_cta.tsx)
    const shouldAutoSave = wasFollowPromptParamPresent &&
      followPrompt &&
      !loading &&
      canFavorite &&
      !favoriteFromQuery.id &&
      favoriteFromQuery.queryParams &&
      favoriteFromQuery.searchableType !== reverb_feed_SearchableType.FAVORITE_SHOP;

    if (!shouldAutoSave) {
      return;
    }

    if (user.loggedOut) {
      redirectToSignin('FavoriteButton', LocationUtil.currentPageWithParams({ [FOLLOW_PROMPT_PARAM]: true }));
      return;
    }

    setFollowPrompt(false);
    autoSaveFavorite('FollowPrompt');
  }
  React.useEffect(processAutoSaveIfEligible, [loading, serializedQueryData]);

  const autoSaveFavorite = async (trackingName: string) => {
    return autoSave(favoriteFromQuery.queryParams, trackingName);
  };

  return {
    isToastOpen,
    setIsToastOpen,
    isFavorite,
    canFavorite,
    favorite,
    setFavorite,
    autoSaveFavorite,
    hasError,
    setHasError,
    errorMessage,
    setErrorMessage,
  };
}

export const FIND_FAVORITE_QUERY = gql(`
  query Core_FindFavorite(
    $listingsSearchRequest: Input_reverb_search_ListingsSearchRequest
    $shopSlug: String
  ) {
    findFavorite(
      input: {
        listingsSearchRequest: $listingsSearchRequest
        shopSlug: $shopSlug
      }
    ) {
      isFavorite
      canFavorite
      favorite {
        id
        favorited
        searchableId
        searchableType
        title
        emailEnabled
        feedEnabled
        queryParams
        subtitle
        link {
          href
        }
      }
    }
  }
`);

export type ExternalProps = React.PropsWithChildren<{
  displayStyle?: 'button' | 'banner' | 'link' | 'muted-button'
  iconButtonSize?: RCFavoriteButtonProps['size'];
  iconButtonVariant?: RCFavoriteButtonProps['variant'];
  ignoreFollowPrompt?: boolean;
  header?: string;
  subtext?: string;
  showCallout?: boolean;
  componentName?: string;
  condensed?: boolean;
}>;

export type BaseProps = ExternalProps & IUserContext & WithRouterProps & WithUrlProps;
export type ConnectedProps = ChildProps<BaseProps, Core_FindFavoriteQuery, Core_FindFavoriteQueryVariables>;

const withFindFavoriteQuery =
  withGraphql<
    BaseProps,
    Core_FindFavoriteQuery,
    Core_FindFavoriteQueryVariables
  >(
    FIND_FAVORITE_QUERY,
    {
      options: (props: BaseProps) => {
        let listingsSearchRequest = mapVarsForMarketplaceQuery(props).inputListings;
        let shopSlug;

        if (isShopPage(props.url) && hasNoFiltersApplied(props.url)) {
          listingsSearchRequest = null;
          shopSlug = props.params.shop;
        }

        const shouldSsr = !isCSP(props.url);

        return {
          ssr: shouldSsr,
          variables: {
            listingsSearchRequest,
            shopSlug,
          },
          fetchPolicy: 'network-only',
        };
      },
    },
  );

export function InternalFindFavoriteProvider(props: ConnectedProps) {
  const { url, data, children } = props;
  // memoize whether `follow_prompt=true` was initially in the URL query params,
  // for the given `[url.pathname]`, like "/marketplace".
  // When `[url.pathname]` changes, such as from "/marketplace" to "/p/slug", this gets re-evaluated.
  const wasFollowPromptParamPresent = React.useMemo(
    function () {
      return !!url.query[FOLLOW_PROMPT_PARAM];
    },
    [
      url.pathname,
      sortAndStringifyParams(omit(url.query, FOLLOW_PROMPT_PARAM)),
    ],
  );
  const findFavoriteViewModel = useFindFavoriteProvider(
    data.loading,
    data.findFavorite,
    wasFollowPromptParamPresent,
  );

  const {
    favorite,
    setFavorite,
    isToastOpen,
    setIsToastOpen,
    hasError,
    setHasError,
    errorMessage,
  } = findFavoriteViewModel;

  const handleToastClose = function (closeType: ToastCloseType) {
    setIsToastOpen(false);

    if (closeType === 'dismiss' || closeType === 'swipe') {
      trackEvent(buildClickedToastMessage(url));
    }
  };

  const handleUndoFavoriteRemove = (result: ExecutionResult<UndoDeleteMyFavoriteMutation>) => {
    if (!result.errors.length) {
      setHasError(false);
      setIsToastOpen(true);
      const newFavorite: Core_FindFavoriteQuery_Favorite = {
        ...favorite,
        favorited: result.data.undoDeleteMyFavorite?.favorite?.favorited,
      };
      setFavorite(newFavorite);
    } else {
      setHasError(true);
      setIsToastOpen(true);
    }
  };

  return (
    <FindFavoriteContext.Provider value={findFavoriteViewModel}>
      <FavoriteToast
        favoriteId={favorite.id}
        isFavorited={favorite.favorited}
        favoriteType={core_apimessages_FavoriteType.SAVED_SEARCH}
        isOpen={isToastOpen}
        handleToastClose={handleToastClose}
        handleMutationResult={handleUndoFavoriteRemove}
        hasError={hasError}
        errorMessage={errorMessage}
        setHasError={setHasError}
      />
      {children}
    </FindFavoriteContext.Provider>
  );
}

export function sortAndStringifyParams(params: Object) {
  const orderedParams = Object.keys(params).sort().reduce(
    (obj, key) => {
      obj[key] = params[key];
      return obj;
    },
    {},
  );

  return JSON.stringify(orderedParams);
}

/**
 * Use once in a layout component to keep track of a find favorite result,
 * which will stay up-to-date with the current URL and current user.
 * Consume the context within child components via `useFindFavorite();`
 */
export const FindFavoriteProvider = connect([
  withUserContext,
  withRouter,
  withUrlContext,
  withFindFavoriteQuery,
])(InternalFindFavoriteProvider);
